import React from 'react';
import ContactFormShortcode from './ContactFormShortcode';
import FAQShortcode from './FAQShortcode';
import FAQSectionShortcode from './FAQSectionShortcode';

/**
 * ShortcodeParser - Parses HTML content and replaces shortcodes with React components
 * 
 * Supported shortcodes:
 * - {{contact_form}} - Basic contact form
 * - {{contact_form:"Title", "Subtitle", "Button Text"}} - Contact form with custom texts
 * - {{faq:"Question", "Answer"}} - Single FAQ item
 * - {{faq_section:"Title", "Q1|A1", "Q2|A2"}} - Complete FAQ section
 * 
 * Future shortcodes can include:
 * - {{newsletter}}
 * - {{image:"url"}}
 * - {{gallery:"url1,url2,url3"}}
 * - {{video:"url"}}
 */

const ShortcodeParser = ({ content }) => {
  if (!content) return null;

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  // Parse shortcodes and split content into segments
  const parseContent = () => {
    const shortcodeRegex = /\{\{([^}]+)\}\}/g;
    const segments = [];
    let lastIndex = 0;
    let match;

    while ((match = shortcodeRegex.exec(content)) !== null) {
      // Add HTML content before shortcode
      if (match.index > lastIndex) {
        segments.push({
          type: 'html',
          content: content.substring(lastIndex, match.index),
          key: `html-${lastIndex}`
        });
      }

      // Parse shortcode
      const shortcodeContent = match[1].trim();
      const parsed = parseShortcode(shortcodeContent);
      
      segments.push({
        type: 'shortcode',
        ...parsed,
        key: `shortcode-${match.index}`
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining HTML content
    if (lastIndex < content.length) {
      segments.push({
        type: 'html',
        content: content.substring(lastIndex),
        key: `html-${lastIndex}`
      });
    }

    return segments;
  };

  // Parse individual shortcode
  const parseShortcode = (shortcodeContent) => {
    // Check if it has parameters (contains colon)
    if (shortcodeContent.includes(':')) {
      const [name, paramsString] = shortcodeContent.split(':', 2);
      
      // Parse parameters (comma-separated, quoted strings)
      const params = paramsString
        .split(',')
        .map(p => p.trim())
        .map(p => {
          // Remove surrounding quotes
          if ((p.startsWith('"') && p.endsWith('"')) || 
              (p.startsWith("'") && p.endsWith("'"))) {
            p = p.slice(1, -1);
          }
          // Decode HTML entities in parameters
          return decodeHtmlEntities(p);
        })
        .filter(p => p.length > 0);

      return {
        name: name.trim(),
        params
      };
    }

    return {
      name: shortcodeContent.trim(),
      params: []
    };
  };

  // Render shortcode as React component
  const renderShortcode = (shortcode) => {
    const { name, params } = shortcode;

    switch (name) {
      case 'contact_form':
        return <ContactFormShortcode params={params} />;
      
      case 'faq':
        return <FAQShortcode params={params} />;
      
      case 'faq_section':
        return <FAQSectionShortcode params={params} />;
      
      // Add more shortcodes here
      // case 'newsletter':
      //   return <NewsletterShortcode params={params} />;
      
      default:
        // Unknown shortcode - show as text for debugging
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4 my-4">
            <p className="text-sm text-yellow-800">
              Unknown shortcode: <code className="bg-yellow-100 px-2 py-1 rounded">
                {`{{${name}${params.length > 0 ? `:${params.join(', ')}` : ''}}}`}
              </code>
            </p>
          </div>
        );
    }
  };

  const segments = parseContent();

  return (
    <div className="shortcode-content">
      {segments.map((segment) => {
        if (segment.type === 'html') {
          return (
            <div
              key={segment.key}
              dangerouslySetInnerHTML={{ __html: segment.content }}
            />
          );
        } else if (segment.type === 'shortcode') {
          return (
            <div key={segment.key}>
              {renderShortcode(segment)}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

export default ShortcodeParser;
